// Batch detail (DESIGN §3): "Where is this batch now?"
// Header (name + Batch NN, one StatusBadge, inline Owner/Due, ⋯ menu) → StageProgress → 5 compact stage rows
// (link · status · the one primary action on the current stage) → Notes (auto-save) + Schedule. Right panel: activity.
// Styles: ./batch.css (classes prefixed `bd-`).
import './batch.css';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { DEFAULT_VIDEO_COUNT, MAX_VIDEO_COUNT, STAGES, STAGE_STATUSES, batchLabel, monthLabel } from '../../shared/constants.js';
import { datePart, formatDate, isISODate, isSupportedYear, isoDateOfTimestamp, timePart } from '../../shared/dates.js';
import { compactTime } from '../components/calendar-event.jsx';
import { activityFeed, dueInfo } from '../../shared/derive.js';
import { dispatch, getSnapshot, reload, useStore } from '../store.js';
import { clientHref, clientsHref, navigate, workspaceHref } from '../router.js';
import { Icon } from '../icons.jsx';
import { Avatar, Button, EmptyState, StatusBadge, cx, statusTone, toast } from '../components/ui.jsx';
import { Page } from '../components/page.jsx';
import { Panel } from '../components/panel.jsx';
import { RightPanelSection } from '../components/right-panel.jsx';
import { BRIEFS, StageProgress, briefDone, stageCount, stageSteps, stageTarget } from '../components/stage-progress.jsx';
import { CompactDropdown } from '../components/compact-dropdown.jsx';
import { MenuList, Popover, usePopover } from '../components/popover.jsx';
import { ActivityItem } from '../components/activity-item.jsx';
import { openEditBatch, openEvent } from '../components/forms.jsx';
import { containsEdit, mergeNotes } from '../components/notes-merge.js';
import { clearDraft, isNotesConflict, readDraft, sendOnUnload, writeDraft } from '../components/notes-sync.js';
import { PIC_LABELS, picRoleOf, stagePicId } from '../contract.js';

// ---------- exported helpers ----------

// 'docs.google.com/document/d/1AbC…' → 'docs.google.com/…/1AbC…' style short form for a stage link.
export function shortUrl(url, max = 38) {
  if (!url) return '';
  let text = String(url).replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/[?#].*$/, '').replace(/\/+$/, '');
  if (text.length <= max) return text;
  const slash = text.indexOf('/');
  const host = slash > 0 ? text.slice(0, slash) : text;
  if (host.length >= max - 2) return `${host.slice(0, max - 1)}…`;
  return `${host}/…`;
}

// Errors that mean the page is showing stale data (deleted elsewhere / advanced in another tab): refetch.
export function refreshIfStale(err) {
  if (/not found|moved on|changed by someone else/i.test(err?.message || '')) reload()?.catch?.(() => {});
}

const statusDot = (status) => <span class={cx('badge-dot bd-menu-dot', `tone-${statusTone(status, 'stage')}`)} aria-hidden="true" />;

// ---------- view ----------

export function BatchView({ route }) {
  const { state, index, today } = useStore();
  const batch = index?.batchesById.get(route.params.batchId);
  const deletingRef = useRef(false);
  const notesRef = useRef(null); // Notes registers its flush() here (before Edit opens, so no typed text is lost)

  if (!batch) {
    if (deletingRef.current) return <Page header={null} />;
    return (
      <Page title="Batch not found">
        <EmptyState
          title="This batch doesn't exist."
          description="It may have been deleted, or the link is incorrect."
          action={<Button href={workspaceHref({})} icon="arrowRight">Go to Workspace</Button>}
        />
      </Page>
    );
  }

  const client = index.clientsById.get(batch.clientId);
  const month = index.monthsById.get(batch.monthId);
  const activity = activityFeed(state, index, { batchId: batch.id, limit: 30 });

  const onDelete = () => {
    deletingRef.current = true;
    notesRef.current?.cancel?.();
    const back = client ? clientHref(client.id, month?.monthNumber) : clientsHref();
    return dispatch('batch.delete', { id: batch.id })
      .then(() => {
        toast('Batch deleted.', { tone: 'success' });
        navigate(back.replace(/^#/, ''), { replace: true });
      })
      .catch((err) => { deletingRef.current = false; notesRef.current?.resume?.(); refreshIfStale(err); });
  };

  // Save typed notes first, so the Edit form starts from (and can never write back) older notes.
  const onEdit = () => {
    const id = batch.id;
    Promise.resolve(notesRef.current?.flush?.()).catch(() => {}).then(() => openEditBatch(id));
  };

  return (
    <Page
      class="bd-page"
      header={<BatchHeader batch={batch} index={index} today={today} onEdit={onEdit} onDelete={onDelete} />}
      asideLabel="Batch activity"
      aside={(
        <RightPanelSection title="Activity" empty="No activity yet.">
          {activity.length > 0 && (
            <div class="bd-activity">
              {activity.map((a) => (
                <ActivityItem key={a.id} name={a.actorName} text={a.text} at={a.at} today={today} />
              ))}
            </div>
          )}
        </RightPanelSection>
      )}
    >
      <Pipeline batch={batch} />
      <div class="bd-lower">
        <Notes batch={batch} apiRef={notesRef} />
        <Schedule batch={batch} index={index} today={today} />
      </div>
    </Page>
  );
}

// ---------- header ----------

function BatchHeader({ batch, index, today, onEdit, onDelete }) {
  const month = index.monthsById.get(batch.monthId);
  const client = index.clientsById.get(batch.clientId);
  return (
    <header class="page-header bd-header">
      <div class="page-heading">
        <h1 class="page-title bd-title">
          <span class="bd-title-name">{batch.name}</span>
          <span class="page-title-meta">
            {/* Below 1100px the breadcrumb shortens, so the client link moves here (hidden where the crumb shows it). */}
            {client && <span class="bd-client"><a class="link-quiet" href={clientHref(client.id)}>{client.name}</a> · </span>}
            {/* a batch named just "Batch 01" does not repeat its number */}
            {batch.name?.trim() !== batchLabel(batch.batchNumber) && <>{batchLabel(batch.batchNumber)}{month && ' · '}</>}
            {month && <><span class={cx(month.off && 'bd-month-off')}>{monthLabel(month.monthNumber)}{month.off ? ' · Off' : ''}</span></>}
          </span>
        </h1>
        <div class="bd-facts">
          <span class="bd-fact"><StatusBadge status={batch.status} kind="batch" /></span>
          <span class="bd-fact"><OwnerPicker batch={batch} index={index} /></span>
          <span class="bd-fact"><DueEditor batch={batch} today={today} off={!!month?.off} /></span>
          <span class="bd-fact"><VideosEditor batch={batch} /></span>
        </div>
      </div>
      <div class="page-actions">
        <MoreMenu onEdit={onEdit} onDelete={onDelete} />
      </div>
    </header>
  );
}

// Inline owner: saves immediately, sending only `owner`. Offers active members (plus the current owner).
function OwnerPicker({ batch, index }) {
  const team = [...index.teamById.values()].filter((m) => m.active !== false || m.id === batch.owner).sort((a, b) => a.name.localeCompare(b.name));
  const owner = batch.owner && index.teamById.get(batch.owner);
  const options = [
    ...team.map((m) => ({ value: m.id, label: m.name, meta: m.role, avatar: <Avatar name={m.name} size={20} /> })),
    { value: '', label: 'Unassigned', avatar: <Avatar name="" size={20} /> },
  ];
  const onChange = (value) => {
    dispatch('batch.update', { id: batch.id, patch: { owner: value } }).catch(refreshIfStale);
  };
  return (
    <CompactDropdown
      class="bd-owner"
      label="Owner"
      size="sm"
      value={owner ? owner.id : ''}
      options={options}
      onChange={onChange}
      ariaLabel={`Owner: ${owner ? owner.name : 'Unassigned'}`}
      valueLabel={(
        <span class="bd-owner-value">
          <Avatar name={owner?.name || ''} size={20} />
          <span class="truncate">{owner ? owner.name : 'Unassigned'}</span>
        </span>
      )}
      width={260}
    />
  );
}

// Inline due date: shows "Sep 28" (English, any browser locale) over a transparent native date input.
// Click / Enter / Space opens the picker; a picked date saves at once, typed dates once complete (debounced,
// flushed on blur). Clearing saves on blur. Sends only `dueDate`.
function DueEditor({ batch, today, off }) {
  const [draft, setDraft] = useState(batch.dueDate || '');
  const focused = useRef(false);
  const timer = useRef(0);
  const sent = useRef(batch.dueDate || '');

  useEffect(() => {
    sent.current = batch.dueDate || '';
    if (!focused.current) setDraft(batch.dueDate || '');
  }, [batch.dueDate]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const save = (value) => {
    clearTimeout(timer.current);
    if (value === sent.current) return;
    if (value && !(isISODate(value) && isSupportedYear(value))) return;
    sent.current = value;
    dispatch('batch.update', { id: batch.id, patch: { dueDate: value } })
      .catch((err) => { sent.current = batch.dueDate || ''; setDraft(batch.dueDate || ''); refreshIfStale(err); });
  };
  const openPicker = (input) => { try { input.showPicker?.(); } catch { /* not allowed here: typing still works */ } };

  const valid = isISODate(draft);
  const shown = valid ? formatDate(draft, draft.slice(0, 4) === today.slice(0, 4) ? 'short' : 'long') : 'No date';
  const due = dueInfo({ ...batch, dueDate: valid ? draft : '' }, today, { off });
  const done = batch.status === 'Done' && batch.completedAt ? isoDateOfTimestamp(batch.completedAt) : '';
  return (
    <span class="bd-due">
      <label class="bd-due-field">
        <span class="bd-due-label">Due</span>
        <span class={cx('bd-due-value', !valid && 'is-empty')} aria-hidden="true">
          {shown}
          <Icon name="calendar" size={14} />
        </span>
        <input
          type="date"
          class="bd-due-input"
          value={draft}
          min="2000-01-01"
          max="2100-12-31"
          aria-label={`Due date: ${valid ? formatDate(draft, 'long') : 'none'}`}
          onFocus={() => { focused.current = true; }}
          onClick={(e) => openPicker(e.currentTarget)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(e.currentTarget); } }}
          onInput={(e) => {
            const value = e.currentTarget.value;
            setDraft(value);
            clearTimeout(timer.current);
            if (value) timer.current = setTimeout(() => save(value), 700);
          }}
          onBlur={(e) => { focused.current = false; save(e.currentTarget.value); }}
        />
      </label>
      {due.text && <span class={cx('bd-due-rel', due.tone !== 'neutral' && `text-tone tone-${due.tone}`)}>{due.relative}</span>}
      {done && <span class="bd-due-rel">Completed {formatDate(done, done.slice(0, 4) === today.slice(0, 4) ? 'short' : 'long')}</span>}
    </span>
  );
}

// Inline video count (1–MAX_VIDEO_COUNT): the target of Script, Video Editing and Result. Sends only `videoCount`.
function VideosEditor({ batch }) {
  const value = Number(batch.videoCount) || DEFAULT_VIDEO_COUNT;
  const save = (n) => {
    dispatch('batch.update', { id: batch.id, patch: { videoCount: n } }).catch(refreshIfStale);
  };
  return (
    <label class="bd-videos" title={`Videos in this batch (1 to ${MAX_VIDEO_COUNT})`}>
      <span class="bd-due-label">Videos</span>
      <InlineNumber value={value} min={1} max={MAX_VIDEO_COUNT} label="Videos in this batch" onCommit={save} class="bd-num-field" />
    </label>
  );
}

// "⋯": Edit name/number (the shared Edit Batch form) and Delete with an inline confirm inside the same popover.
function MoreMenu({ onEdit, onDelete }) {
  const pop = usePopover();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef(null);

  useLayoutEffect(() => {
    if (confirming) confirmRef.current?.focus({ preventScroll: true });
  }, [confirming]);

  const close = () => { pop.close(); setConfirming(false); };
  const cancel = () => {
    setConfirming(false);
    requestAnimationFrame(() => pop.anchorRef.current?.focus({ preventScroll: true }));
    pop.close();
  };
  const items = [
    { label: 'Edit batch', icon: 'edit', onSelect: onEdit },
    { divider: true },
    { label: 'Delete batch', icon: 'trash', danger: true, keepOpen: true, onSelect: () => setConfirming(true) },
  ];

  return (
    <>
      <button
        type="button"
        ref={pop.anchorRef}
        class="icon-btn bd-more"
        aria-label="More actions"
        title="More actions"
        aria-expanded={pop.open ? 'true' : 'false'}
        aria-haspopup="true"
        onClick={() => (pop.open ? close() : pop.setOpen(true))}
      >
        <Icon name="more" size={18} />
      </button>
      <Popover
        open={pop.open}
        onClose={close}
        anchorRef={pop.anchorRef}
        placement="bottom-end"
        width={240}
        role={confirming ? 'alertdialog' : 'menu'}
        label={confirming ? 'Delete this batch?' : 'Batch actions'}
        class="popover-menu"
      >
        {confirming ? (
          <div class="bd-confirm" role="group" aria-labelledby="bd-confirm-text">
            <p class="bd-confirm-text" id="bd-confirm-text">Delete this batch and its events? This can't be undone.</p>
            <div class="bd-confirm-actions">
              <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
              <button
                type="button"
                ref={confirmRef}
                class="btn btn-danger btn-sm"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  Promise.resolve(onDelete()).finally(() => { setBusy(false); close(); });
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ) : (
          <MenuList items={items} onClose={close} />
        )}
      </Popover>
    </>
  );
}

// ---------- pipeline ----------

function Pipeline({ batch }) {
  const steps = stageSteps(batch);
  const notStarted = batch.status === 'To Do';
  const current = batch.status === 'Done' ? '' : batch.currentStage;
  const onSelect = (key) => {
    const row = document.getElementById(`bd-stage-${key}`);
    if (!row) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    row.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    row.focus({ preventScroll: true });
  };
  return (
    <section class="panel bd-pipeline" aria-label="Production pipeline">
      <div class="bd-steps">
        <StageProgress batch={batch} variant="full" onSelect={onSelect} />
      </div>
      <ol class="bd-stages">
        {steps.map((step, i) => {
          let action = null;
          if (notStarted) action = i === 0 ? 'start' : null;
          else if (step.stage.key === current) action = 'complete';
          return <StageRow key={step.stage.key} batch={batch} step={step} action={action} />;
        })}
      </ol>
    </section>
  );
}

function StageMark({ state }) {
  if (state === 'done') return <Icon name="check" size={14} />;
  if (state === 'blocked') return <span class="bd-mark-square" />;
  return <span class="bd-mark-dot" />;
}

// One stage: mark · name + tool · link · counter (stages 02/04/05) · status · action.
// Stage 03 has no link of its own: its two briefs follow as sub-rows (done checkmark · name · link).
function StageRow({ batch, step, action }) {
  const { stage, state } = step;
  const status = batch.stageStatus?.[stage.key] || 'To Do';
  const labelId = `bd-stage-label-${stage.key}`;
  const briefs = stage.target === 'briefs';
  return (
    <li
      id={`bd-stage-${stage.key}`}
      tabIndex={-1}
      class={cx('bd-stage', `is-${state}`, action && 'has-action', briefs && 'is-briefs')}
      aria-labelledby={labelId}
      aria-current={step.isCurrent ? 'step' : undefined}
    >
      <span class="bd-stage-mark" aria-hidden="true"><StageMark state={state} /></span>
      <span class="bd-stage-name">
        <span class="bd-stage-label" id={labelId}>
          <span class="bd-stage-num">{stage.number}</span> {stage.label}
        </span>
        <span class="bd-stage-tool">
          {stage.tool}{briefs ? ` · ${step.count}/${step.target} done` : ''}
          {!briefs && <StagePic batch={batch} stageKey={stage.key} />}
        </span>
      </span>
      {!briefs && <StageLink batch={batch} field={stage.urlField} label={stage.label} tool={stage.tool} />}
      {stage.target === 'videos' && <span class="bd-stage-count"><Counter batch={batch} stage={stage} /></span>}
      <span class="bd-stage-status">
        <StageStatus batch={batch} stage={stage} status={status} />
      </span>
      <span class="bd-stage-action">
        {action && <StageAction batch={batch} kind={action} />}
      </span>
      {briefs && BRIEFS.map((b) => <BriefRow key={b.key} batch={batch} brief={b} tool={stage.tool} />)}
    </li>
  );
}

// Stage 03 sub-row: done checkmark · brief name · its own link. A Done stage 03 has both ticked (locked).
function BriefRow({ batch, brief, tool }) {
  const done = briefDone(batch, brief.key);
  const locked = batch.stageStatus?.videoShootEditingBrief === 'Done';
  const [busy, setBusy] = useState(false);
  const toggle = () => {
    if (busy || locked) return;
    setBusy(true);
    dispatch('batch.setBrief', { id: batch.id, which: brief.key, done: !done })
      .catch(refreshIfStale)
      .finally(() => setBusy(false));
  };
  return (
    <div class={cx('bd-brief', done && 'is-done')}>
      <span class="bd-brief-name">
        <button
          type="button"
          role="checkbox"
          class="bd-check"
          aria-checked={done ? 'true' : 'false'}
          aria-disabled={locked ? 'true' : undefined}
          title={locked ? 'Stage done' : done ? 'Mark not done' : 'Mark done'}
          onClick={toggle}
        >
          <Icon name="check" size={12} />
          <span class="sr-only">{brief.label} done</span>
        </button>
        <span class="bd-brief-label">{brief.label}</span>
      </span>
      {/* the brief's PIC leads the link cell, so the brief name keeps its room */}
      <span class="bd-brief-end">
        <StagePic batch={batch} stageKey={brief.key} compact />
        <StageLink batch={batch} field={brief.urlField} label={brief.label} tool={tool} />
      </span>
    </div>
  );
}

// The PIC of a stage (or a stage-03 brief) from the client's team (DESIGN §9): small avatar + first name; nothing when unset.
// `compact` (stage-03 brief rows, where the brief name needs the room): the avatar alone, the name in its tooltip.
function StagePic({ batch, stageKey, compact = false }) {
  const { index } = useStore();
  const person = index?.teamById.get(stagePicId(batch, stageKey, index));
  if (!person) return null;
  const role = PIC_LABELS[picRoleOf(stageKey)] || 'PIC';
  return (
    <span class="bd-pic" title={`${role}: ${person.name}`}>
      <Avatar name={person.name} size={16} />
      {compact ? <span class="sr-only">{person.name}</span> : <span class="bd-pic-name">{person.name.split(/\s+/)[0]}</span>}
      <span class="sr-only">, {role}</span>
    </span>
  );
}

// "12 / 15" with −/+ (one click each) and a typeable number. Shows the new count at once while saves queue up.
// −/+ are relative: each save is computed from the freshest stored count (re-read first when this view is older than 5 s)
// and also sent as `delta`, so a stale page never overwrites someone else's counting. Typed numbers are absolute.
// A Done stage reads full and is locked (reopen it via its status to edit).
const COUNTER_FRESH_MS = 5000;
function Counter({ batch, stage }) {
  const target = stageTarget(batch, stage.key);
  const stored = stageCount(batch, stage.key);
  const locked = batch.stageStatus?.[stage.key] === 'Done';
  const [shown, setShown] = useState(null);
  const inflight = useRef(0);
  const chain = useRef(Promise.resolve());
  const synced = useRef(0);
  const value = Math.min(target, shown ?? stored);
  const send = (n, delta = 0) => {
    const next = Math.min(target, Math.max(0, n));
    if (locked || next === value) return;
    const from = value; // what this page showed: a typed number is refused if someone else changed it meanwhile
    setShown(next);
    inflight.current += 1;
    const { id } = batch;
    const key = stage.key;
    const run = chain.current.then(async () => {
      if (delta && Date.now() - synced.current > COUNTER_FRESH_MS) await reload()?.catch?.(() => {});
      const fresh = getSnapshot().index?.batchesById.get(id) || batch;
      const count = delta ? Math.min(stageTarget(fresh, key), Math.max(0, stageCount(fresh, key) + delta)) : next;
      return dispatch('batch.setProgress', delta ? { id, stage: key, count, delta } : { id, stage: key, count, from });
    });
    chain.current = run.catch(() => {});
    run
      .then(() => { synced.current = Date.now(); }, refreshIfStale)
      .finally(() => {
        inflight.current -= 1;
        if (inflight.current <= 0) { inflight.current = 0; setShown(null); }
      });
  };
  return (
    <span class={cx('bd-counter', locked && 'is-locked', value >= target && 'is-full')} role="group" aria-label={`${stage.label} progress`}>
      {!locked && (
        <button type="button" class="bd-counter-btn" aria-label={`${stage.label}: one less`} title="One less" disabled={value <= 0} onClick={() => send(value - 1, -1)}>
          <span aria-hidden="true">−</span>
        </button>
      )}
      <InlineNumber
        value={value}
        min={0}
        max={target}
        disabled={locked}
        label={`${stage.label} done, out of ${target}`}
        onCommit={send}
      />
      <span class="bd-counter-target" aria-hidden="true">/ {target}</span>
      {!locked && (
        <button type="button" class="bd-counter-btn" aria-label={`${stage.label}: one more`} title="One more" disabled={value >= target} onClick={() => send(value + 1, 1)}>
          <span aria-hidden="true">+</span>
        </button>
      )}
    </span>
  );
}

// A tiny whole-number field: click and type, Enter/blur saves, Esc restores. Out-of-range input restores too.
function InlineNumber({ value, min, max, label, disabled, onCommit, class: cls }) {
  const [draft, setDraft] = useState(String(value));
  const focused = useRef(false);
  const skip = useRef(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(() => { if (!focused.current) setDraft(String(value)); }, [value]);
  const commit = () => {
    const text = draftRef.current.trim();
    const n = Number(text);
    if (!text || !Number.isInteger(n) || n < min || n > max) { setDraft(String(value)); return; }
    if (n !== value) onCommit(n);
  };
  return (
    <input
      class={cx('bd-num', cls)}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={String(max).length}
      value={draft}
      aria-label={label}
      readOnly={disabled}
      tabIndex={disabled ? -1 : undefined}
      onFocus={(e) => { focused.current = true; if (!disabled) e.currentTarget.select(); }}
      onInput={(e) => setDraft(e.currentTarget.value.replace(/\D/g, '').slice(0, String(max).length))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); skip.current = true; setDraft(String(value)); e.currentTarget.blur(); }
      }}
      onBlur={() => {
        focused.current = false;
        if (skip.current) { skip.current = false; return; }
        if (!disabled) commit();
      }}
    />
  );
}

// Tiny per-stage status dropdown (To Do / In Progress / Review / Blocked / Done). Done on the CURRENT stage is the same
// as Mark Complete (the next stage starts and its PIC is told), so approving from Review never stalls the pipeline.
function StageStatus({ batch, stage, status }) {
  const options = STAGE_STATUSES.map((s) => ({ value: s, label: s, avatar: statusDot(s) }));
  const onChange = (value) => {
    if (value === 'Done' && batch.status !== 'Done' && stage.key === batch.currentStage) {
      dispatch('batch.advance', { id: batch.id, stage: stage.key })
        .then(() => toast('Stage completed.', { tone: 'success' }))
        .catch(refreshIfStale);
      return;
    }
    dispatch('batch.setStageStatus', { id: batch.id, stage: stage.key, status: value, from: status }).catch(refreshIfStale);
  };
  return (
    <CompactDropdown
      class="bd-status"
      size="sm"
      value={status}
      options={options}
      onChange={onChange}
      placement="bottom-end"
      width={200}
      ariaLabel={`${stage.label} status: ${status}`}
      valueLabel={<StatusBadge status={status} kind="stage" class="badge-plain" />}
    />
  );
}

// The one primary action: Start (nothing started yet, first row) or Mark Complete (current stage). One click.
function StageAction({ batch, kind }) {
  const [busy, setBusy] = useState(false);
  const start = kind === 'start';
  const run = () => {
    if (busy) return;
    setBusy(true);
    const request = start
      ? dispatch('batch.startProduction', { id: batch.id })
      : dispatch('batch.advance', { id: batch.id, stage: batch.currentStage });
    request
      .then(() => {
        toast(start ? 'Production started.' : 'Stage completed.', { tone: 'success' });
        // The button moves to the next row (or disappears): keep keyboard focus on the pipeline.
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (active && active !== document.body) return;
          const next = document.querySelector('.bd-stage.has-action .btn-primary') || document.getElementById(`bd-stage-${STAGES[STAGES.length - 1].key}`);
          next?.focus?.({ preventScroll: true });
        });
      })
      .catch(refreshIfStale)
      .finally(() => setBusy(false));
  };
  return (
    <Button variant="primary" size="sm" icon={start ? 'play' : 'check'} disabled={busy} onClick={run}>
      {start ? 'Start' : 'Mark Complete'}
    </Button>
  );
}

// Link of a stage (or of one of stage 03's briefs): "docs.google.com/… · Open ↗ · ✎", or "+ Add link" that turns into an
// inline input. Enter/blur saves (only that URL field), Esc cancels; a rejected URL keeps the editor open with the reason.
function StageLink({ batch, field, label, tool }) {
  const url = batch[field] || '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const settled = useRef(false);
  const refocus = useRef(false);

  useLayoutEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (refocus.current) {
      refocus.current = false;
      triggerRef.current?.focus();
    }
  }, [editing]);

  const open = () => { settled.current = false; setDraft(url); setError(''); setEditing(true); };
  const close = (restoreFocus) => {
    settled.current = true;
    refocus.current = !!restoreFocus;
    setEditing(false);
    setError('');
  };
  const commit = (viaKeyboard) => {
    if (settled.current || saving) return;
    const value = draft.trim();
    if (value === url) { close(viaKeyboard); return; }
    settled.current = true;
    setSaving(true);
    dispatch('batch.update', { id: batch.id, patch: { [field]: value } }, { toast: false })
      .then(() => {
        setSaving(false);
        close(viaKeyboard);
        toast(value ? 'Link saved.' : 'Link removed.', { tone: 'success' });
      })
      .catch((err) => {
        setSaving(false);
        settled.current = false;
        setError(err?.message || 'Enter a valid link.');
        refreshIfStale(err);
        if (viaKeyboard) inputRef.current?.focus();
      });
  };

  if (editing) {
    const keep = (e) => e.preventDefault(); // pressing Save/Cancel must not blur-save first
    const errorId = `bd-link-error-${field}`;
    return (
      <span
        key="edit"
        class="bd-stage-link bd-link-editor"
        ref={wrapRef}
        onFocusOut={(e) => {
          if (!wrapRef.current?.contains(e.target)) return; // e.g. the "Add link" button being replaced
          if (!document.hasFocus()) return; // switched apps to copy the URL: not done editing
          if (!wrapRef.current?.contains(e.relatedTarget)) commit(false);
        }}
      >
        <span class="bd-link-row">
          <input
            ref={inputRef}
            class={cx('input input-sm bd-link-input', error && 'is-invalid')}
            type="url"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            value={draft}
            placeholder={tool === 'Google Drive' ? 'Paste a Google Drive link' : 'Paste a Google Docs link'}
            aria-label={`${label} link`}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? errorId : undefined}
            readOnly={saving}
            onInput={(e) => { setDraft(e.currentTarget.value); if (error) setError(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(true); }
              else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
            }}
          />
          <button type="button" class="icon-btn icon-btn-sm bd-link-btn" aria-label="Save link" title="Save" onMouseDown={keep} onClick={() => commit(true)}>
            <Icon name="check" size={15} />
          </button>
          <button type="button" class="icon-btn icon-btn-sm bd-link-btn" aria-label="Cancel" title="Cancel" onMouseDown={keep} onClick={() => close(true)}>
            <Icon name="x" size={15} />
          </button>
        </span>
        {error && <span class="bd-link-error" id={errorId} role="alert">{error}</span>}
      </span>
    );
  }

  if (!url) {
    return (
      <span key="add" class="bd-stage-link">
        <button ref={triggerRef} type="button" class="bd-link-add" onClick={open} aria-label={`Add ${label} link`}>
          <Icon name="plus" size={14} />
          Add link
        </button>
      </span>
    );
  }

  return (
    <span key="view" class="bd-stage-link">
      <span class="bd-link-url" title={url}>{shortUrl(url)}</span>
      <a class="bd-link-open" href={url} target="_blank" rel="noopener noreferrer">
        Open
        <Icon name="arrowUpRight" size={14} />
        <span class="sr-only"> {label} (opens in a new tab)</span>
      </a>
      <button ref={triggerRef} type="button" class="icon-btn icon-btn-sm bd-link-btn" aria-label={`Edit ${label} link`} title="Edit link" onClick={open}>
        <Icon name="edit" size={14} />
      </button>
    </span>
  );
}

// ---------- notes ----------

const NOTES_DELAY = 600;
const NOTES_FRESH_MS = 5000;
const SAVE_TEXT = { saving: 'Saving…', saved: 'Saved', error: 'Not saved' };
const clean = (text) => (text || '').trim(); // the server stores notes trimmed
const mergedNotice = () => toast('Someone else edited these notes. Both changes were kept.', { duration: 5000 });

// Auto-saves while typing (debounced 600 ms); flushed on blur, route change, Edit, tab hidden and page unload (keepalive).
// Never overwrites someone else's newer text: a save first re-reads a view older than 5 s and replays my edit onto the
// newer text (notes-merge.js), and sends `notesBase` (the text my edit started from) for the server to check.
// Unsaved text stays on this device until a save is confirmed and is restored after signing in again (notes-sync.js).
function Notes({ batch, apiRef }) {
  const stored = batch.notes || '';
  const id = batch.id;
  const draftKey = `batch:${id}`;
  const [draft, setDraft] = useState(stored);
  const [saveState, setSaveState] = useState('');
  const latest = useRef(stored); // the textarea text
  const base = useRef(stored); // the stored (trimmed) text `latest` was typed on
  const sending = useRef(null); // a value sent whose result is not known yet
  const unloadSent = useRef(null);
  const synced = useRef(0); // when the stored text was last known to be fresh
  const chain = useRef(Promise.resolve());
  const timer = useRef(0);
  const cancelled = useRef(false);
  const pending = () => clean(latest.current) !== base.current;

  const refreshing = useRef(null);
  const refresh = () => {
    synced.current = Date.now();
    const p = Promise.resolve(reload()).catch(() => {});
    refreshing.current = p;
    p.then(() => { if (refreshing.current === p) refreshing.current = null; });
    return p;
  };
  const show = (value) => { latest.current = value; setDraft(value); };
  // Newer stored text: follow it when nothing is typed, else replay the typed edit onto it. → true when merged.
  const rebase = (fresh) => {
    if (fresh === base.current) return false;
    if ([latest.current, sending.current, unloadSent.current].some((t) => t != null && fresh === clean(t))) { base.current = fresh; return false; }
    if (!pending()) { base.current = fresh; show(fresh); clearDraft(draftKey); return false; }
    const merged = mergeNotes(base.current, latest.current, fresh);
    base.current = fresh;
    show(merged);
    writeDraft(draftKey, merged, fresh);
    return true;
  };
  useEffect(() => { if (rebase(stored)) mergedNotice(); }, [stored]);

  const saveNow = async (retry) => {
    if (cancelled.current || !pending()) return;
    if (!getSnapshot().index?.batchesById.has(id)) return;
    setSaveState('saving');
    if (Date.now() - synced.current > NOTES_FRESH_MS) refresh();
    if (refreshing.current) await refreshing.current;
    // Replay my edit onto the newest text this page knows (a refresh, another tab's save…) before sending.
    const fresh = getSnapshot().index?.batchesById.get(id);
    if (!fresh || cancelled.current) { setSaveState(''); return; }
    if (rebase(fresh.notes || '')) mergedNotice();
    if (!pending()) { setSaveState(''); return; }
    const value = latest.current;
    sending.current = value;
    try {
      await dispatch('batch.update', { id, patch: { notes: value, notesBase: base.current } }, { toast: false });
      synced.current = Date.now();
      base.current = clean(value);
      if (!pending()) { clearDraft(draftKey); setSaveState('saved'); } else setSaveState('');
    } catch (err) {
      if (isNotesConflict(err) && retry) { synced.current = 0; await saveNow(false); return; }
      setSaveState('error');
      if (getSnapshot().status === 'ready') toast(err?.message || 'Notes not saved.', { tone: 'danger' }); // a 401 is toasted by the store
      refreshIfStale(err);
    } finally {
      if (sending.current === value) sending.current = null;
    }
  };
  // Saves now (after any save in flight). Never rejects.
  const flush = () => {
    clearTimeout(timer.current);
    timer.current = 0;
    if (cancelled.current || !pending()) return chain.current;
    chain.current = chain.current.then(() => saveNow(true)).catch(() => {});
    return chain.current;
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  apiRef.current = {
    flush,
    cancel: () => { cancelled.current = true; clearTimeout(timer.current); clearDraft(draftKey); },
    resume: () => { cancelled.current = false; },
  };

  // Signed out mid-edit (401), crashed or closed before the save: restore this device's unsaved text and save it.
  useEffect(() => {
    const saved = readDraft(draftKey);
    if (!saved) return;
    if (clean(saved.value) === stored || (saved.base !== stored && containsEdit(saved.base, saved.value, stored))) { clearDraft(draftKey); return; }
    const value = saved.base === stored ? saved.value : mergeNotes(saved.base, saved.value, stored);
    show(value);
    writeDraft(draftKey, value, stored);
    toast('Unsaved notes restored.', { duration: 4000 });
    timer.current = setTimeout(() => flushRef.current(), 0);
  }, []);

  useEffect(() => {
    // The page is going away (closed, reloaded, tab hidden on a phone): a keepalive request outlives it.
    const leave = () => {
      const value = latest.current;
      if (cancelled.current || !pending() || value === unloadSent.current) return;
      clearTimeout(timer.current);
      unloadSent.current = value;
      sendOnUnload('batch.update', { id, patch: { notes: value, notesBase: base.current } });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') leave();
      else if (pending()) { clearTimeout(timer.current); timer.current = setTimeout(() => flushRef.current(), NOTES_DELAY); }
    };
    window.addEventListener('beforeunload', leave);
    window.addEventListener('pagehide', leave);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', leave);
      window.removeEventListener('pagehide', leave);
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(timer.current);
      flushRef.current(); // leaving the batch (route change): keep the text
    };
  }, []);

  useEffect(() => {
    if (saveState !== 'saved') return undefined;
    const t = setTimeout(() => setSaveState(''), 2000);
    return () => clearTimeout(t);
  }, [saveState]);

  const onInput = (e) => {
    const value = e.currentTarget.value;
    latest.current = value;
    setDraft(value);
    writeDraft(draftKey, value, base.current);
    if (saveState === 'saved' || saveState === 'error') setSaveState('');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flushRef.current(), NOTES_DELAY);
  };
  // Start from the server's text: a view that may be stale re-reads before the first keystroke lands.
  const onFocus = () => {
    if (pending() || Date.now() - synced.current <= NOTES_FRESH_MS) return;
    refresh();
  };

  return (
    <Panel
      class="bd-notes"
      title="Notes"
      actions={(
        <span id="bd-notes-status" class={cx('bd-save', saveState && `is-${saveState}`)} role="status" aria-live="polite">
          {saveState === 'saved' && <Icon name="check" size={13} />}
          {SAVE_TEXT[saveState] || ''}
        </span>
      )}
    >
      <textarea
        class="textarea bd-notes-input"
        rows={6}
        maxLength={5000}
        value={draft}
        placeholder="Client feedback, delivery specs, blockers…"
        aria-label="Notes"
        aria-describedby="bd-notes-status"
        onInput={onInput}
        onFocus={onFocus}
        onBlur={() => flushRef.current()}
      />
    </Panel>
  );
}

// ---------- schedule ----------

// Default event type for "+ Event" from the batch's current stage.
const STAGE_EVENT_KIND = {
  briefPremisScript: 'Script Deadline',
  script: 'Script Deadline',
  videoShootEditingBrief: 'Shoot Date',
  videoEditing: 'Editing Deadline',
  result: 'Result Deadline',
};

function eventWhen(event, today) {
  const date = datePart(event.start);
  const day = date === today ? 'Today' : formatDate(date, date.slice(0, 4) === today.slice(0, 4) ? 'weekday' : 'long');
  let time = 'All day';
  if (!event.allDay && timePart(event.start)) time = compactTime(timePart(event.start));
  const last = event.end ? datePart(event.end) : date;
  return { date, day, time, past: last < today };
}

function Schedule({ batch, index, today }) {
  const events = [...(index.eventsByBatch.get(batch.id) || [])]
    .sort((a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title));
  const addEvent = () => openEvent({
    defaults: {
      clientId: batch.clientId,
      monthId: batch.monthId,
      batchId: batch.id,
      stage: batch.currentStage,
      owner: batch.owner,
      calendarType: 'main',
      kind: STAGE_EVENT_KIND[batch.currentStage],
    },
  });
  return (
    <Panel
      class="bd-schedule"
      title="Schedule"
      meta={events.length ? String(events.length) : ''}
      flush
      actions={<Button variant="ghost" size="sm" icon="plus" onClick={addEvent}>Event</Button>}
    >
      {events.length === 0 ? (
        <p class="bd-empty">No events yet.</p>
      ) : (
        <ul class="bd-events" aria-label="Batch schedule">
          {events.map((event) => {
            const when = eventWhen(event, today);
            const internal = event.calendarType === 'internal';
            return (
              <li key={event.id}>
                <button
                  type="button"
                  class={cx('bd-event', when.past && 'is-past', when.date === today && 'is-today')}
                  onClick={() => openEvent({ event })}
                  aria-label={`${event.title}, ${when.day}, ${when.time}. Edit event`}
                >
                  <span class="bd-event-when">
                    <span class="bd-event-day">{when.day}</span>
                    <span class="bd-event-time">{when.time}</span>
                  </span>
                  <span class={cx('bd-event-dot', internal ? 'is-internal' : 'is-main')} aria-hidden="true" />
                  <span class="bd-event-title truncate">{event.title}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
